import * as React from 'react';

import type { EmbedProps } from './index';
import VideoTimeline from './VideoTimeline';
import { parseTimeParams } from '../utils/urls';

const NativeVideo: React.FC<EmbedProps> & { isMatch: (href: string) => boolean } = ({ attrs }) => {
  const { href } = attrs;
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const { start: startTime, end: endTime } = parseTimeParams(href);

  // State for video metadata
  const [duration, setDuration] = React.useState(0);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const hasSetInitialTime = React.useRef(false);

  // Handle video time updates and timestamp functionality
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    // Create AbortController for event management
    const abortController = new AbortController();
    const { signal } = abortController;

    const handleLoadStart = () => {
      // Reset current time when video starts loading
      setCurrentTime(0);
      hasSetInitialTime.current = false;
      setIsLoading(true);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      if (startTime !== null && startTime !== undefined && startTime >= 0 && !hasSetInitialTime.current) {
        // Set initial time but only once to prevent loops
        hasSetInitialTime.current = true;
        video.currentTime = startTime;
        setCurrentTime(startTime);
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    // Add event listeners with AbortController signal
    video.addEventListener('loadstart', handleLoadStart, { signal });
    video.addEventListener('loadedmetadata', handleLoadedMetadata, { signal });
    video.addEventListener('canplay', handleCanPlay, { signal });
    video.addEventListener('timeupdate', handleTimeUpdate, { signal });

    return () => {
      // Abort all event listeners at once
      abortController.abort();
    };
  }, [startTime, endTime]);

  return (
    <div className="relative w-full">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        controls
        playsInline
        width="100%"
        height="auto"
        src={href}
        title="Native Video"
        className="overflow-hidden rounded-lg"
        controlsList="nodownload"
      >
        Your browser does not support the video tag.
      </video>
      {React.useMemo(() => {
        if (duration > 0 && !isLoading && (startTime !== undefined || endTime !== undefined)) {
          return (
            <VideoTimeline
              duration={duration}
              currentTime={currentTime}
              startTime={startTime ?? null}
              endTime={endTime ?? null}
            />
          );
        }

        return null;
      }, [duration, isLoading, startTime, endTime, currentTime])}
    </div>
  );
};

// Static method to check if the href matches a native video URL
NativeVideo.isMatch = (href: string): boolean => {
  const [url] = href.split('?');
  const videoExtensions = /\.(mp4|webm|ogg|mov|m4v|avi|flv|wmv|mkv)(?:\?.*|#.*)?$/i;

  if (url && videoExtensions.test(url)) {
    return true;
  }

  return false;
};

export default NativeVideo;
